/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks\useSystemGeneralData.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the System General module.
────────────────────────────────────────────────────────────────*/


import { useState, useEffect, useCallback, useMemo } from 'react';
import { DEFAULT_LANGUAGE_CODE, DEFAULT_VOICE } from '../../../data/formOptions';
import {
  SystemGeneralSettings,
  SystemGeneralSettingsPayload,
} from '../types/systemGeneral.types';
import {
  fetchSystemGeneralSettings,
  updateSystemGeneralSettings,
  InvalidDomainError,
} from '../api';
import { useContextStore } from '../../../store/context';

/**
 * Options for the useSystemGeneralData hook.
 */
interface UseSystemGeneralDataOptions {
  /** The currently selected domain ID. If null, the hook will wait until a domain is selected. */
  domainId: number | null | undefined;
  /** Callback fired upon successful load or save. */
  onSuccess?: (data: SystemGeneralSettings) => void;
  /** Callback fired upon failure. */
  onError?: (error: Error) => void;
}

/**
 * Custom React hook for managing System General data.
 * Handles loading from API, saving changes, tracking state, and deriving defaults.
 * @param options - Configuration options for the hook.
 */
export function useSystemGeneralData(options: UseSystemGeneralDataOptions) {
  const { domainId, onSuccess, onError } = options;

  const [data, setData] = useState<SystemGeneralSettings | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const initialSettings: SystemGeneralSettings = useMemo(
    () => ({
      domainId: domainId || 1,
      defaultLanguage: DEFAULT_LANGUAGE_CODE,
      defaultVoice: DEFAULT_VOICE,
      defaultTimezone: 'America/New_York',
      inboundCodecPrefs: 'PCMU,PCMA',
      outboundCodecPrefs: 'PCMU,PCMA',
      maxRegistrations: 5,
      dtmfMode: 'rfc4733',
      voicemailEnabled: true,
      voicemailMaxMessages: 100,
      voicemailNotificationEnabled: true,
      voicemailTranscribeEnabled: false,
      voicemailEmailAttachment: true,
      recordIncoming: false,
      recordOutgoing: false,
      recordInternal: false,
      recordOnDemand: false,
      recordingFormat: 'wav',
      ringTimeout: 30,
      callWaiting: true,
      privacy: 'none',
      maxCalls: 0,
      rejectAnonymous: false,
      holdMusic: 'local_stream://default',
      ringtone: '',
      callScreen: false,
      pinlessDialing: false,
      autoPasswordLength: 20,
    }),
    [domainId]
  );

  const deriveDefaultVoice = useCallback((language?: string) => {
    if (!language) {
      return DEFAULT_VOICE;
    }
    if (language.includes('es')) {
      return 'paloma';
    }
    return DEFAULT_VOICE;
  }, []);

  const cloneSettings = useCallback(
    (settings: SystemGeneralSettings): SystemGeneralSettings => {
      // Deep clone to avoid reference issues
      return JSON.parse(JSON.stringify(settings));
    },
    []
  );

  const loadSettings = useCallback(async () => {
    if (!domainId) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const result = await fetchSystemGeneralSettings(domainId);

      if (result.success && result.data) {
        const normalizedLanguage =
          typeof result.data.defaultLanguage === 'string'
            ? result.data.defaultLanguage || DEFAULT_LANGUAGE_CODE
            : DEFAULT_LANGUAGE_CODE;

        const loadedData: SystemGeneralSettings = {
          ...result.data,
          domainId,
          defaultLanguage: normalizedLanguage,
          defaultVoice: result.data.defaultVoice || deriveDefaultVoice(normalizedLanguage),
        };

        const cloned = cloneSettings(loadedData);
        setData(cloned);
        onSuccess?.(cloned);
      }
    } catch (err) {
      if (err instanceof InvalidDomainError) {
        // Stale domain in localStorage — clear it so user can re-select
        useContextStore.getState().setSelectedDomain(null);
      }
      const error = err instanceof Error ? err : new Error('Failed to load settings');
      setError(error);
      onError?.(error);
    } finally {
      setIsLoading(false);
    }
  }, [domainId, cloneSettings, deriveDefaultVoice, onSuccess, onError]);

  const saveSettings = useCallback(
    async (payload: SystemGeneralSettingsPayload) => {
      if (!domainId) {
        throw new Error('Domain ID is required');
      }

      try {
        setIsSaving(true);
        setError(null);

        const result = await updateSystemGeneralSettings(domainId, payload);

        if (result.success && result.data) {
          const normalizedLanguage =
            typeof result.data.defaultLanguage === 'string'
              ? result.data.defaultLanguage || DEFAULT_LANGUAGE_CODE
              : DEFAULT_LANGUAGE_CODE;

          const savedData: SystemGeneralSettings = {
            ...result.data,
            domainId,
            defaultLanguage: normalizedLanguage,
            defaultVoice: result.data.defaultVoice || deriveDefaultVoice(normalizedLanguage),
          };

          const cloned = cloneSettings(savedData);
          setData(cloned);
          onSuccess?.(cloned);
          return cloned;
        }

        throw new Error('Failed to save settings');
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to save settings');
        setError(error);
        onError?.(error);
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [domainId, cloneSettings, deriveDefaultVoice, onSuccess, onError]
  );

  useEffect(() => {
    if (domainId) {
      loadSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainId]);

  return {
    data,
    isLoading,
    isSaving,
    error,
    initialSettings,
    loadSettings,
    saveSettings,
    deriveDefaultVoice,
    cloneSettings,
  };
}

